import { queryOptions } from '@tanstack/react-query';
import { type AccessUser, permissions, type UserRole } from '@utilities-one/api-sdk';

import { actions } from '@/actions';
import { userStatusNames } from '@/config/users';
import { hasPermission } from '@/entities/user/utils/access';
import { api, parseResponse } from '@/library/api/honoClient';
import type { GetAllFilter, GetAllQuery } from '@/library/db/types';

export const userQueryKeys = {
  all: ['users'] as const,
  grids: () => [...userQueryKeys.all, 'grid'] as const,
  grid: (query: GetAllQuery) => [...userQueryKeys.grids(), query] as const,
  gridTotals: () => [...userQueryKeys.all, 'total'] as const,
  gridTotal: (filter: GetAllFilter) => [...userQueryKeys.gridTotals(), filter] as const,
  details: () => [...userQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...userQueryKeys.details(), id] as const,
  session: () => [...userQueryKeys.all, 'session'] as const,
  accessUser: (userId: string, sessionId: string) => [...userQueryKeys.all, 'accessUser', userId, sessionId] as const,

  // Simple (no params)
  allNames: () => [...userQueryKeys.all, 'allNames'] as const,
  accountants: () => [...userQueryKeys.all, 'accountants'] as const,
  cc: () => [...userQueryKeys.all, 'cc'] as const,
  coordinatorsByPermissions: () => [...userQueryKeys.all, 'coordinatorsByPermissions'] as const,
  foremanByPermission: () => [...userQueryKeys.all, 'foremanByPermission'] as const,
  allOpm: () => [...userQueryKeys.all, 'allOpm'] as const,
  productionManagers: () => [...userQueryKeys.all, 'productionManagers'] as const,
  productionManagersByPermissions: () => [...userQueryKeys.all, 'productionManagersByPermissions'] as const,
  technicianByPermission: () => [...userQueryKeys.all, 'technicianByPermission'] as const,
  internalUsers: () => [...userQueryKeys.all, 'internalUsers'] as const,
  vp: () => [...userQueryKeys.all, 'vp'] as const,
  dailyCheckInUsers: () => [...userQueryKeys.all, 'dailyCheckInUsers'] as const,
  technicians: (filter?: GetAllFilter) => [...userQueryKeys.all, 'technicians', filter ?? {}] as const,

  // Parameterized
  coordinators: (directorId?: string) => [...userQueryKeys.all, 'coordinators', directorId ?? 'none'] as const,
  directors: (marketId?: string) => [...userQueryKeys.all, 'directors', marketId] as const,
  directorByProjectAllNames: (projectId: string) =>
    [...userQueryKeys.all, 'directorByProjectAllNames', projectId] as const,
  foremansByProjectId: (projectId: string) => [...userQueryKeys.all, 'foremansByProjectId', projectId] as const,
  foremans: (filter?: Record<string, unknown>) => [...userQueryKeys.all, 'foremans', filter] as const,
  opms: (directorId?: string) => [...userQueryKeys.all, 'opms', directorId] as const,
  byRoles: (roles: UserRole[]) => [...userQueryKeys.all, 'byRoles', roles] as const,
  allByCompanyId: (companyId: string) => [...userQueryKeys.all, 'allByCompanyId', companyId] as const,
  selectStatus: () => [...userQueryKeys.all, 'select', 'status'] as const,
  selectPermissionsType: () => [...userQueryKeys.all, 'select', 'permissionsType'] as const,
};

export const userGridQueryOptions = (query: GetAllQuery) =>
  queryOptions({
    queryKey: userQueryKeys.grid(query),
    queryFn: () => actions.users.getAll(query),
  });

export const userGridTotalQueryOptions = (filter: GetAllFilter) =>
  queryOptions({
    queryKey: userQueryKeys.gridTotal(filter),
    queryFn: () => actions.users.getTotal(filter),
  });

// Grid via Hono API (AgGrid)
export const userAgGridQueryOptions = (query: GetAllQuery) =>
  queryOptions({
    queryKey: [...userQueryKeys.grid(query), 'api'] as const,
    queryFn: () => parseResponse(api.users.getAll.$post({ json: query })),
  });

export const userAgGridTotalQueryOptions = (filter: GetAllFilter) =>
  queryOptions({
    queryKey: [...userQueryKeys.gridTotal(filter), 'api'] as const,
    queryFn: () => parseResponse(api.users.getTotal.$post({ json: filter })),
  });

export const userAgGridUnifiedQueryOptions = (query: GetAllQuery) =>
  queryOptions({
    queryKey: [...userQueryKeys.grid(query), 'api', 'unified'] as const,
    queryFn: async () => {
      const [items, total] = await Promise.all([
        parseResponse(api.users.getAll.$post({ json: query })),
        parseResponse(api.users.getTotal.$post({ json: query.filter })),
      ]);
      return [items, total] as const;
    },
  });

// Detail
export const userDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: userQueryKeys.detail(id),
    queryFn: () => actions.users.getById(id),
    enabled: !!id,
  });

/** Current browser session identity (`getSessionUser`). Used by `AccessProvider`. */
export const userSessionUserQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.session(),
    queryFn: () => actions.users.getSessionUser(),
  });

/** Full `AccessUser` for the signed-in session (user + session flags). */
export const userAccessUserQueryOptions = (params: { userId: string; sessionId: string }) =>
  queryOptions({
    queryKey: userQueryKeys.accessUser(params.userId, params.sessionId),
    queryFn: async (): Promise<AccessUser | null> => {
      try {
        const sessionUser = await actions.users.getById(params.userId);
        if (!sessionUser) return null;

        const sessionDetails = await actions.sessions.getUserSession();

        let accessUser: AccessUser = { ...sessionUser, sessionId: params.sessionId };

        if (hasPermission(accessUser, permissions.projects.view.asTechnician)) {
          try {
            const resolved = await parseResponse(api.users.resolveTechnicianPermissions.$post({}));

            accessUser = { ...accessUser, permissions: resolved.permissions as typeof accessUser.permissions };
          } catch (error) {
            console.error('Failed to resolve parent permissions:', error);
          }
        }

        console.log(1, accessUser);

        accessUser.isImpersonating = sessionDetails?.impersonatedById != null;

        return accessUser;
      } catch (error) {
        console.error('Failed to sync session user:', error);
        return null;
      }
    },
  });

// Simple
export const userAllNamesQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.allNames(),
    queryFn: () => actions.users.getAllNames(),
  });

export const userAccountantsQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.accountants(),
    queryFn: () => actions.users.getAccountants(),
  });

export const userCCQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.cc(),
    queryFn: () => actions.users.getCC(),
  });

export const userCoordinatorsByPermissionsQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.coordinatorsByPermissions(),
    queryFn: () => actions.users.getCoordinatorsByPermissions(),
  });

export const userForemanByPermissionQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.foremanByPermission(),
    queryFn: () => actions.users.getForemanByPermission(),
  });

export const userAllOpmQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.allOpm(),
    queryFn: () => actions.users.getAllOpm(),
  });

export const userProductionManagersQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.productionManagers(),
    queryFn: () => actions.users.getProductionManagers(),
  });

export const userProductionManagersByPermissionsQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.productionManagersByPermissions(),
    queryFn: () => actions.users.getProductionManagersByPermissions(),
  });

export const userTechnicianByPermissionQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.technicianByPermission(),
    queryFn: () => actions.users.getTechnicianByPermission(),
  });

export const userInternalUsersQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.internalUsers(),
    queryFn: () => actions.users.getInternalUsers(),
  });

export const userVPQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.vp(),
    queryFn: () => actions.users.getVP(),
  });

export const userDailyCheckInUsersQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.dailyCheckInUsers(),
    queryFn: () => actions.users.getDailyCheckInUsers(),
  });

export const userTechniciansQueryOptions = (filter?: GetAllFilter) =>
  queryOptions({
    queryKey: userQueryKeys.technicians(filter),
    queryFn: () => actions.users.getTechnicians(filter),
  });

// Parameterized
export const userCoordinatorsQueryOptions = (directorId?: string) =>
  queryOptions({
    queryKey: userQueryKeys.coordinators(directorId),
    queryFn: () => actions.users.getCoordinators(directorId),
  });

export const userDirectorsQueryOptions = (marketId?: string, ensureItem?: { id: string; name: string }) =>
  queryOptions({
    queryKey: [...userQueryKeys.directors(marketId), 'ensure', ensureItem?.id ?? null] as const,
    queryFn: async () => {
      const list = await actions.users.getDirectors(marketId);
      if (!ensureItem || list.some((d) => d.id === ensureItem.id)) return list;
      return [...list, ensureItem].sort((a, b) => a.name.localeCompare(b.name));
    },
  });

export const userDirectorByProjectAllNamesQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: userQueryKeys.directorByProjectAllNames(projectId),
    queryFn: () => actions.users.getDirectorByProjectAllNames(projectId),
    enabled: !!projectId,
  });

export const userForemansByProjectIdQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: userQueryKeys.foremansByProjectId(projectId),
    queryFn: () => actions.users.getForemansByProjectId(projectId),
    enabled: !!projectId,
  });

export const userForemansQueryOptions = (filter?: Record<string, unknown>) =>
  queryOptions({
    queryKey: userQueryKeys.foremans(filter),
    queryFn: () => actions.users.getForemans(filter),
  });

export const userOpmsQueryOptions = (directorId?: string) =>
  queryOptions({
    queryKey: userQueryKeys.opms(directorId),
    queryFn: () => actions.users.getOpms(directorId),
  });

export const userByRolesQueryOptions = (roles: UserRole[]) =>
  queryOptions({
    queryKey: userQueryKeys.byRoles(roles),
    queryFn: () => actions.users.getByRoles(roles),
    enabled: roles.length > 0,
  });

export const userAllByCompanyIdQueryOptions = (companyId: string) =>
  queryOptions({
    queryKey: userQueryKeys.allByCompanyId(companyId),
    queryFn: () => actions.users.getAllByCompanyId(companyId),
    enabled: !!companyId,
  });

export const userStatusSelectQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.selectStatus(),
    queryFn: () => Promise.resolve(Object.entries(userStatusNames).map(([id, name]) => ({ id, name }))),
  });

const permissionTypeOptions = [
  { id: 'custom', name: 'Custom Permissions' },
  { id: 'nonScoped', name: 'Non Company Scoped' },
  { id: 'scoped', name: 'Company Scoped' },
] as const;

export const userPermissionsTypeSelectQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.selectPermissionsType(),
    queryFn: async () => [...permissionTypeOptions],
  });
